import React, { useEffect } from 'react'

import { ButtonType } from 'src/apollo/sites'
import { reportError } from 'src/lib/js/clientError'
import { t, Trans } from 'src/translations'

import Image from 'shared/components/common/Image'
import { BrandedButton } from 'shared/components/common/button'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useModal
} from 'shared/components/common/modal'
import { usePopoverContext } from 'shared/components/common/popover/PopoverContext'

import { CART_POPOVER_CONTEXT_KEY } from 'public/components/default_template/online_ordering/cart/CartModal'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useTimeBasedRules } from 'public/components/online_ordering/TimeBasedRuleContext'
import { ItemSource } from '../cart/cartUtils'

type Props = {
  channelGuid?: string
}

export const ItemResolutionModal = ({ channelGuid }: Props) => {
  const { isOpen, onClose, onOpen } = useModal()
  const { addToCart, deleteFromCart } = useCart()
  const { fulfillmentData } = useFulfillment()
  const {
    itemToAdd,
    setItemToAdd,
    conflictingItemInCart,
    setConflictingItemInCart
  } = useTimeBasedRules()
  const cartContext = usePopoverContext(CART_POPOVER_CONTEXT_KEY)

  useEffect(() => {
    if (itemToAdd) {
      onOpen()
    }
  }, [onOpen, itemToAdd])

  // This really shouldn't happen
  if (!itemToAdd || !conflictingItemInCart) {
    return null
  }

  const closeModal = () => {
    setItemToAdd(undefined)
    setConflictingItemInCart(undefined)
    onClose()
  }

  const handleAddItem = async () => {
    if (conflictingItemInCart.selectionGuid) {
      await deleteFromCart(conflictingItemInCart.selectionGuid)
    }

    const trackData = {
      quantity: itemToAdd.quantity,
      price: itemToAdd.modifier?.price,
      unitOfMeasure: itemToAdd.modifier?.unitOfMeasure,
      timeBasedRule: 'Preorder'
    }

    try {
      await addToCart(
        itemToAdd.modifier,
        itemToAdd.quantity,
        fulfillmentData?.cartFulfillmentData,
        trackData,
        channelGuid,
        ItemSource.Menu
      )
    } catch (error) {
      reportError('Error adding cart item.', error)
      return
    }

    cartContext?.open()
    closeModal()
  }

  return (
    <Modal isOpen={isOpen} onClose={closeModal} testId='item-resolution-modal'>
      <ModalOverlay fadeIn />
      <ModalContent
        contentClassName='itemResolutionModal'
        fadeIn
        ariaLabelledBy='dining-options-modal-header'
      >
        <div className='header'>
          <h3 id='dining-options-modal-header' tabIndex={-1}>
            {t('item-resolution-modal.oops')}
          </h3>
          <ModalCloseButton />
        </div>
        <div className='info'>
          <Image src='icons/warning-orange.svg' alt='' />
          <div>
            {/* {Item 1} and {Item 2} are not available to order at the same time. */}
            {conflictingItemInCart.name ? (
              itemToAdd.name ? (
                <Trans
                  i18nKey='item-resolution-modal.items-not-available-1'
                  components={{
                    b: <b />,
                    span: <span />
                  }}
                  values={{
                    nameOfExistingItem: conflictingItemInCart.name,
                    nameOfNewItem: itemToAdd.name
                  }}
                />
              ) : (
                <Trans
                  i18nKey='item-resolution-modal.items-not-available-2'
                  components={{
                    b: <b />,
                    span: <span />
                  }}
                  values={{
                    nameOfExistingItem: conflictingItemInCart.name
                  }}
                />
              )
            ) : itemToAdd.name ? (
              <Trans
                i18nKey='item-resolution-modal.items-not-available-3'
                components={{
                  b: <b />,
                  span: <span />
                }}
                values={{
                  nameOfNewItem: itemToAdd.name
                }}
              />
            ) : (
              <Trans
                i18nKey='item-resolution-modal.items-not-available-4'
                components={{
                  span: <span />
                }}
              />
            )}
          </div>
        </div>
        <div className='actions'>
          <div>
            {/* Do you want to add {Item 2} to your cart?  If so, we will remove {Item 1} from your cart. */}
            {itemToAdd.name ? (
              conflictingItemInCart.name ? (
                <Trans
                  i18nKey='item-resolution-modal.do-you-want-to-add-1'
                  components={{
                    b: <b />,
                    span: <span />
                  }}
                  values={{
                    nameOfExistingItem: conflictingItemInCart.name,
                    nameOfNewItem: itemToAdd.name
                  }}
                />
              ) : (
                <Trans
                  i18nKey='item-resolution-modal.do-you-want-to-add-2'
                  components={{
                    b: <b />,
                    span: <span />
                  }}
                  values={{
                    nameOfNewItem: itemToAdd.name
                  }}
                />
              )
            ) : conflictingItemInCart.name ? (
              <Trans
                i18nKey='item-resolution-modal.do-you-want-to-add-3'
                components={{
                  b: <b />,
                  span: <span />
                }}
                values={{
                  nameOfExistingItem: conflictingItemInCart.name
                }}
              />
            ) : (
              <Trans
                i18nKey='item-resolution-modal.do-you-want-to-add-4'
                components={{
                  span: <span />
                }}
              />
            )}
          </div>
          <BrandedButton variant={ButtonType.Primary} onClick={handleAddItem}>
            {t('item-resolution-modal.add-to-cart')}
          </BrandedButton>
          <BrandedButton variant={ButtonType.Secondary} onClick={onClose}>
            {t('item-resolution-modal.cancel')}
          </BrandedButton>
        </div>
      </ModalContent>
    </Modal>
  )
}
